import React, { useState, useEffect,useRef } from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";


const MainContainer = styled(Box)(({ theme }) => ({
  background: "#000",
  display: "flex",
  flexDirection: "column",
  padding: "60px 24px",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "95%",
    height: "80%",
    left: "65%",
    top: "10%",
    bottom: "10%",
    transform: "translateX(-100%), translateY(-50%)",
    background:
      "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    //backdropFilter: "blur(500px)",
    zIndex: 0,
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  width: "100%", // Set to 95% width
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const StatsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 1,
  width: "100%",
  //background: "rgba(140, 140, 194, 0.1)",
  borderRadius: theme.spacing(1),
  overflow: "hidden",
}));

const StatCell = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    right: 0,
    top: "10%",
    height: "80%",
    width: "1px",
    background: "rgba(140, 140, 194, 0.2)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "10%",
    width: "80%",
    height: "1px",
    background: "rgba(140, 140, 194, 0.2)",
  },
  "&:nth-of-type(2n)::after": {
    display: "none",
  },
  "&:nth-last-of-type(-n+2)::before": {
    display: "none",
  },
}));


const AnimatedCounter = ({ value, start }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!start) {
      setDisplayValue(0); // Reset when section leaves viewport
      return;
    }

    const numericValue = parseFloat(
      value.replace(/,/g, "").replace(/[+%]/g, "")
    );

    const hasDecimal = value.includes(".");
    const duration = 1000;
    const interval = 16;

    let startTime;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min(
        (timestamp - startTime) / duration,
        1
      );

      const currentValue = numericValue * progress;

      setDisplayValue(
        hasDecimal
          ? currentValue.toFixed(2)
          : Math.floor(currentValue)
      );

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(
          hasDecimal
            ? numericValue.toFixed(2)
            : numericValue
        );
      }
    };

    requestAnimationFrame(animate);
  }, [start, value]);

  const hasPlus = value.includes("+");
  const hasPercent = value.includes("%");

  return (
    <>
      {Number(displayValue).toLocaleString()}
      {hasPercent && "%"}
      {hasPlus && "+"}
    </>
  );
};



const WeAreIED = ({ solution = false, title = null, subtitle = null }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stats = [
    { value: "150,000", label: "Users Empowered" },
    { value: "5", label: "Fortune 500 Served" },
    { value: "99.99%", label: "Platform Reliability" },
    { value: "5,000", label: "Processes Streamlined" },
  ];

const statsRef = useRef(null);
const [isVisible, setIsVisible] = useState(false);

useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      setIsVisible(entry.isIntersecting);
    },
    {
      threshold: 0.4, // Trigger when 40% visible
    }
  );

  if (statsRef.current) {
    observer.observe(statsRef.current);
  }

  return () => {
    if (statsRef.current) {
      observer.unobserve(statsRef.current);
    }
  };
}, []);

  return (
    <MainContainer>
      <ContentContainer>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width={isMobile ? "100%" : "100%"}
          margin="0 auto"
        >
          {/* Header Section */}
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            sx={{ width: { xs: "100%", md: "70%" } }}
            textAlign="center"
          >
            <Typography
              variant="h2"
              sx={{
                background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "24px", sm: "32px", md: "40px" },
                fontWeight: 700,
                mb: { xs: 2, md: 4 },
                textAlign: "center",
              }}
            >
              {solution ? "Unlock the Potential of Your Assets Today with Us" : title ? title : "Built on Trust. Driven by Results"}
            </Typography>
            <Typography
              sx={{
                color: "#8C8CC2",
                fontSize: { xs: "14px", md: "16px" },
                lineHeight: 1.8,
                textAlign: { xs: "left", md: "center" },
                marginTop: 2,
                marginBottom: { xs: 5, md: 10 },
              }}
            >
              {subtitle ? subtitle : "Partnering with enterprises to strengthen operations, improve accountability, and deliver measurable value through trusted execution and industry expertise."}
            </Typography>
          </Box>

          {/* iENERGY Section */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap={4}
            width="100%"
            justifyContent="space-between"
            alignItems={{ md: "center", xs: "flex-start" }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box width={{ xs: "100%", md: "95%" }}>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "white",
                      fontSize: {
                        xs: "24px",
                        sm: "32px",
                        md: "40px",
                      },
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    We are{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "#33BFFF",
                        fontWeight: 700,
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      IENERGY

                      <Box
                        component="sup"
                        sx={{
                          position: "absolute",
                          top: {
                            xs: "-8px",
                            md: "-8px",
                          },
                          right: {
                            xs: "-16px",
                            md: "-16px",
                          },
                          fontSize: {
                            xs: "28px",
                            md: "32px",
                          },
                          fontWeight: 700,
                          color: "#33BFFF",
                          lineHeight: 1,
                        }}
                      >
                        ®
                      </Box>
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      color: "#8C8CC2",
                      fontSize: { xs: "16px", md: "18px" },
                      lineHeight: 1.8,
                      fontWeight: 600,
                    }}
                  >
                    We believe successful transformation is built on trust, collaboration, and long-term commitment. We work closely with enterprises to solve operational challenges, strengthen governance, and deliver practical solutions that create measurable business value.

                    Our approach combines industry understanding, execution discipline, and partnership-driven engagement to help organizations operate with greater confidence and efficiency.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} ref={statsRef}>
                <StatsGrid>
                  {stats?.map((stat, index) => (
                    <StatCell key={index}>
                      <Typography
                        sx={{
                          color: "#61D1FA",
                          fontSize: { xs: "24px", md: "48px" },
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        <AnimatedCounter
                          value={stat.value}
                          start={isVisible}
                        />
                      </Typography>

                      <Typography
                        sx={{
                          color: "#8C8CC2",
                          fontSize: { xs: "16px", md: "20px" },
                          fontWeight: 600,
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </StatCell>
                  ))}
                </StatsGrid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </ContentContainer>
    </MainContainer>
  );
};

export default WeAreIED;
